import { ColDef, GridApi, IRowNode } from "ag-grid-community";
import { action, makeObservable, observable } from "mobx";
import ProgressIndicatorModel, {
  IProgressIndicatorModel
} from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import PermissionsContext from "../../../../../../contexts/permissions/PermissionsContext";
import { PermissionFields } from "../../../../../../contexts/permissions/PermissionsTypes";
import { EntityTypes, GridTypes, TrainingCourseField } from "../../../../../../enums";
import { getEntityNameMicroFormFields } from "../../../../../../pages/change/forms/microForm/getEntityNameMicroFormFields";
import { SingleFormModel } from "../../../../../../pages/change/forms/singleFormModel/SingleForm_model";
import ProjectTeamUserPermissionsApi, {
  ProjectTeamUserPermissionsApi as IProjectTeamUserPermissionsApi
} from "../../../../../../services/api/v2/projectTeamUserPermissions/ProjectTeamUserPermissions.api";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import { IGridToastService } from "../../../../../../services/local/gridToastService/IGridToastService";
import I18n from "../../../../../localization/I18n";
import { IModalContextModel } from "../../../../../modalZ/context/IModalContext";
import ModalContext from "../../../../../modalZ/context/ModalContext";
import ToasterService, { IToasterService } from "../../../../../toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../toaster/Toaster_model";
import { GridModalBuilder } from "../../modals/GridModal_builder";
import { IGridUiAction } from "../base/AppGridToolbarActions_view";
import { AppGridState } from "../base/AppGrid_view";
import { ColumnStateAddon } from "../base/addons/ColumnStateAddon";
import { FilterStoreAddon } from "../base/addons/FilterAddon/FilterStoreAddon";
import { TextWrapperAddon } from "../base/addons/TextWrapAddon";
import { GetTrainingCoursesGridActions } from "./TrainingCoursesGrid_actions";
import {
  LINK_TO_ACTIONS_MODAL_CONFIG,
  LINK_TO_PROCESSES_MODAL_CONFIG,
  SHOW_CONFIRM_CREATION_MODAL,
  SHOW_TRAINING_MODULE_DELETE_CONFIRM_MODAL
} from "./TrainingCoursesGrid_modals";
import { AppGridToolbarType } from "../../types/AppGrid_types";
import { TrainingCoursesGridColumnBuilder } from "./TrainingCoursesGrid_columns";
import {
  CommonColDefFieldNamesEnum,
  TrainingCourseColDefFieldNamesEnum,
  TrainingModuleColDefFieldNamesEnum
} from "../../../enums/AgGridColDefFieldNameEnum";
import TrainingCoursesApi, {
  TrainingCoursesApi as ITrainingCoursesApi
} from "../../../../../../services/api/v2/trainingCourses/TrainingCourses.api";
import TrainingTypesApi, {
  TrainingTypesApi as ITrainingTypesApi
} from "../../../../../../services/api/v2/trainingTypes/TrainingTypes.api";
import TrainingProgressApi, {
  TrainingProgressApi as ITrainingProgressApi
} from "../../../../../../services/api/v2/trainingProgress/TrainingProgress.api";

export class TrainingCoursesGridModel {
  organisationId: number;
  projectId: number;
  progressStatuses: FP.Generic.IKeyLabel[];
  trainingCoursesProvider: ITrainingCoursesApi;
  authUser: FP.Entities.IUser;
  filterStoreAddon: FilterStoreAddon;
  columnStateAddon: ColumnStateAddon;
  textWrapAddon: TextWrapperAddon;
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  userCanViewTrainingCourses: boolean;
  userCanEditTrainingCourses: boolean;
  userCanViewActions: boolean;
  userCanViewProcesses: boolean;
  type: GridTypes = GridTypes.TRAINING_MODULES_GRID;
  trainingCoursesGridColumnBuilder: TrainingCoursesGridColumnBuilder;
  modalService: IModalContextModel;
  httpProgress: IProgressIndicatorModel;
  @observable confirmationService: IModalContextModel;
  @observable selectedItems: number[] = [];
  toasterService: IToasterService;
  @observable isDuplicatingItems: boolean = false;
  @observable microTrainingCourseForm: SingleFormModel;
  gridApi: GridApi;
  @observable.ref gridActions: IGridUiAction[] = [];
  gridModalBuilder: GridModalBuilder;
  gridToastService: IGridToastService;
  projectTeamUserPermissionsProvider: IProjectTeamUserPermissionsApi = ProjectTeamUserPermissionsApi;
  trainingProgressesProvider: ITrainingProgressApi = TrainingProgressApi;
  trainingTypesProvider: ITrainingTypesApi = TrainingTypesApi;
  @observable projectTeamMembers: any[];
  @observable trainingProgresses: any[]; //todo: add types
  @observable trainingTypes: any[];
  gridColumns: ColDef<any, any>[];
  urlSearchParams: URLSearchParams;
  gridToolbarType: AppGridToolbarType;
  gridType: EntityTypes | GridTypes;
  @observable isLoading: boolean = true;
  onFieldUpdate: () => void;
  refreshDataHandler?: any;

  constructor(
    organisationId: number,
    projectId: number,
    authUser: FP.Entities.IUser,
    urlSearchParams: URLSearchParams,
    gridToolbarType: AppGridToolbarType,
    preSelectedItemIds: number[],
    gridType: EntityTypes | GridTypes,
    onFieldUpdate: () => void,
    refreshDataHandler?: any
  ) {
    makeObservable(this);
    this.trainingCoursesProvider = TrainingCoursesApi;
    this.onFieldUpdate = onFieldUpdate;
    this.refreshDataHandler = refreshDataHandler;
    this.userCanViewTrainingCourses = PermissionsContext.canViewField(
      PermissionFields.PROJECTS,
      organisationId,
      projectId
    );
    this.userCanEditTrainingCourses = PermissionsContext.canEditField(
      PermissionFields.PROJECTS,
      organisationId,
      projectId
    );
    this.userCanViewActions = PermissionsContext.canViewField(PermissionFields.ACTIONS, organisationId, projectId);
    this.userCanViewProcesses = PermissionsContext.canViewField(PermissionFields.PROCESSES, organisationId, projectId);
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.authUser = authUser;
    this.urlSearchParams = urlSearchParams;
    this.gridToolbarType = gridToolbarType;
    this.modalService = ModalContext;
    this.toasterService = ToasterService;
    this.confirmationService = ModalContext;
    this.httpProgress = ProgressIndicatorModel;
    this.setMicroAddForm();
    this.gridToastService = GridToastService;
    this.gridModalBuilder = new GridModalBuilder();
    this.selectedItems = preSelectedItemIds;
    this.gridType = gridType;
    this.generateAddons();
  }

  loadInitData = async () => {
    if (this.userCanEditTrainingCourses && this.gridToolbarType !== "link-modal") {
      await this.loadProjectTeamMembers();
      await this.loadTrainingProgresses();
      await this.loadTrainingTypes();
      this.generateFn();
    }
  };

  @action
  onMount = async () => {
    this.generateFn();
    await this.loadInitData();
    this.setIsLoading(false);
  };

  @action
  generateFn = () => {
    this.generateGridConfig();
    this.generateActions();
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  onUnmount = () => {};

  generateAddons = () => {
    this.textWrapAddon = new TextWrapperAddon(this.projectId, EntityTypes.TRAINING_MODULES);
    this.filterStoreAddon = new FilterStoreAddon({
      projectId: this.projectId,
      gridType: EntityTypes.TRAINING_MODULES,
      filterHasChangedFn: this.setIsFilterChanged,
      urlSearchParams: this.urlSearchParams
    });

    this.columnStateAddon = new ColumnStateAddon({
      projectId: this.projectId,
      gridType: EntityTypes.TRAINING_MODULES,
      columnOrderHasChangedFn: this.setIsColumnStateChanged
    });
  };

  generateGridConfig = () => {
    this.trainingCoursesGridColumnBuilder = new TrainingCoursesGridColumnBuilder({
      canEdit: this.userCanEditTrainingCourses && this.gridToolbarType !== "link-modal",
      organisationId: this.organisationId,
      projectId: this.projectId,
      userCanViewTrainingCourses: this.userCanViewTrainingCourses,
      projectTeamMembers: this.projectTeamMembers,
      onFieldUpdate: this.onFieldUpdate,
      trainingProgresses: this.trainingProgresses,
      trainingTypes: this.trainingTypes,
      columns: [
        CommonColDefFieldNamesEnum.Selected,
        CommonColDefFieldNamesEnum.Name,
        CommonColDefFieldNamesEnum.Description,
        TrainingCourseColDefFieldNamesEnum.TrainingProgress,
        TrainingCourseColDefFieldNamesEnum.TrainingType,
        TrainingModuleColDefFieldNamesEnum.Duration,
        CommonColDefFieldNamesEnum.StartDate,
        CommonColDefFieldNamesEnum.EndDate,
        CommonColDefFieldNamesEnum.Owner,
        TrainingCourseColDefFieldNamesEnum.Code,
        TrainingCourseColDefFieldNamesEnum.ProjectStakeholderOwners,
        TrainingCourseColDefFieldNamesEnum.TrainingModuleCount,
        TrainingCourseColDefFieldNamesEnum.IsSignedOff,
        this.userCanViewActions && CommonColDefFieldNamesEnum.Actions,
        this.userCanViewProcesses && CommonColDefFieldNamesEnum.Processes,
        CommonColDefFieldNamesEnum.NoteCount,
        CommonColDefFieldNamesEnum.Tags,
        CommonColDefFieldNamesEnum.CreatedBy,
        CommonColDefFieldNamesEnum.CreatedAt,
        CommonColDefFieldNamesEnum.ModifiedBy,
        CommonColDefFieldNamesEnum.UpdatedAt
      ]
    });
    this.gridColumns = this.trainingCoursesGridColumnBuilder.generateColumnDefs();
  };

  generateActions = () => {
    this.setGridActions(GetTrainingCoursesGridActions(this));
  };

  // #region Initial data loading methods
  @action
  loadProjectTeamMembers = async () => {
    const res = await this.projectTeamUserPermissionsProvider.getAllUsersSimple(this.organisationId, this.projectId);
    if (!res || res.isError) return;
    this.setProjectTeamMembers(res.payload);
  };

  @action
  setProjectTeamMembers = projectTeamMembers => {
    this.projectTeamMembers = projectTeamMembers;
  };

  @action
  loadTrainingProgresses = async () => {
    const res = await this.trainingProgressesProvider.getAll(this.organisationId);
    if (!res || res.isError) return;
    this.setTrainingProgresses(res.payload);
  };

  @action
  setTrainingProgresses = trainingProgresses => {
    this.trainingProgresses = trainingProgresses;
  };

  @action
  loadTrainingTypes = async () => {
    const res = await this.trainingTypesProvider.getAll(this.organisationId);
    if (!res || res.isError) return;
    this.setTrainingTypes(res.payload);
  };

  @action
  setTrainingTypes = trainingTypes => {
    this.trainingTypes = trainingTypes;
  };
  // #endregion

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };

  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.gridApi = gridState.gridApi;
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setIsColumnStateChanged = (isColumnStateChanged: boolean) => {
    this.isColumnStateChanged = isColumnStateChanged;
    this.generateActions();
  };

  @action
  setIsFilterChanged = (isFilterChanged: boolean) => {
    this.isFilterChanged = isFilterChanged;
    this.generateActions();
  };

  @action
  deleteFieldData = async (itemId: number, field: TrainingCourseField) => {
    const res = await this.trainingCoursesProvider
      .deleteField(this.organisationId, this.projectId, itemId, field)
      .then(async e => {
        await this.onFieldUpdate();
        return e;
      });

    if (res?.isError) return false;
    this.gridToastService.showToast(res.code, res.message);

    return true;
  };

  removeItems = async (itemIds: number[]) => {
    this.httpProgress.showOverlay();
    let res = await this.trainingCoursesProvider
      .deleteRange(this.organisationId, this.projectId, itemIds)
      .then(async e => await this.refreshDataHandler());

    this.httpProgress.hideOverlay();
    if (!res || res.isError) return;

    return res.payload;
  };

  @action
  setSelectedNodes = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.selectedItems?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  exportParams = () => {
    return {
      onlySelected: true,
      fileName: "insight-training-courses-export.csv"
    };
  };

  @action
  exportRows = () => {
    if (this.selectedItems && this.selectedItems.length > 0) {
      if (this.gridApi !== undefined) this.gridApi.exportDataAsCsv(this.exportParams());
    }
  };

  @action
  setMicroAddForm = () => {
    this.microTrainingCourseForm = new SingleFormModel();
    this.microTrainingCourseForm.formFields = getEntityNameMicroFormFields(
      this.createMicroTrainingCourse,
      I18n.t("placeholders.myNewName", { entity: I18n.t("entities.trainingCourse") })
    );
  };

  @action
  createMicroTrainingCourse = async () => {
    if (!this.microTrainingCourseForm.isSaving) {
      let microTrainingCourseFormRes = await this.microTrainingCourseForm.submit();
      this.microTrainingCourseForm.isSaving = true;
      if (!microTrainingCourseFormRes) {
        this.microTrainingCourseForm.isSaving = false;
        return;
      }

      microTrainingCourseFormRes = {
        ...microTrainingCourseFormRes,
        projectId: this.projectId,
        actions: [],
        processes: []
      };

      this.httpProgress.showOverlay();
      let trainingCourseNameExists = await this.trainingCoursesProvider.getByProjectIdAndName(
        this.organisationId,
        this.projectId,
        microTrainingCourseFormRes.name
      );

      if (trainingCourseNameExists && !trainingCourseNameExists.isError && trainingCourseNameExists.payload.length) {
        this.httpProgress.hideOverlay();
        let confirmCreateTrainingCourse = await this.confirmCreateTrainingCourse(microTrainingCourseFormRes.name);
        if (!confirmCreateTrainingCourse) {
          this.microTrainingCourseForm.isSaving = false;
          return;
        }
        this.httpProgress.showOverlay();
      }

      const res = {
        ...microTrainingCourseFormRes
      };
      this.httpProgress.showOverlay();
      const result = await this.trainingCoursesProvider
        .create(this.organisationId, this.projectId, res as FP.Entities.ITrainingCourse)
        .then(e => this.refreshDataHandler());

      this.httpProgress.hideOverlay();

      if (!result || result.isError) {
        this.microTrainingCourseForm.isSaving = false;
        return;
      }
      this.microTrainingCourseForm.isSaving = false;

      const trainingCourse = result.payload;
      this.microTrainingCourseForm.resetFields();
      this.toasterService
        .showSuccessToast()
        .setContent(
          <span>{I18n.t("phrases.itemCreatedSuccessfully", { item: I18n.t("entities.trainingCourse") })}</span>
        )
        .startTimer(TOASTER_TOAST_TIME.NORMAL);
      return trainingCourse;
    }
  };

  //#region Grid Action Toolbar Modals Methods [#11111190]
  confirmCreateTrainingCourse = async (name: string): Promise<boolean> => {
    return SHOW_CONFIRM_CREATION_MODAL(name);
  };

  showTrainingCourseConfirmDeleteModal = () => {
    return SHOW_TRAINING_MODULE_DELETE_CONFIRM_MODAL(this.selectedItems, this.removeItems);
  };

  assignActionsToTrainingCourses = async actionIds => {
    await this.trainingCoursesProvider
      .addBulkActionsToTrainingCourses(this.organisationId, this.projectId, this.selectedItems, actionIds)
      .then(e => this.onFieldUpdate());

    this.modalService.hide();
  };

  assignProcessesToTrainingCourses = async projectProcessIds => {
    await this.trainingCoursesProvider
      .addBulkProjectProcessesToTrainingCourses(
        this.organisationId,
        this.projectId,
        this.selectedItems,
        projectProcessIds
      )
      .then(e => this.onFieldUpdate());

    this.modalService.hide();
  };

  //#endregion

  //#region Grid Link Action Modal Methods [#11122290]
  showLinkToActions = () => {
    this.gridModalBuilder
      .constructPopupModal()
      .setModalOptions(LINK_TO_ACTIONS_MODAL_CONFIG(this.assignActionsToTrainingCourses))
      .generateModal();
  };
  showLinkToProcesses = () => {
    this.gridModalBuilder
      .constructPopupModal()
      .setModalOptions(LINK_TO_PROCESSES_MODAL_CONFIG(this.selectedItems, this.assignProcessesToTrainingCourses))
      .generateModal();
  };

  //#endregion

  //#region Grid Link Action Methods [#11133390]
  //#endregion
}
